import { Button, ButtonGroup } from "react-bootstrap";
import { URLSearchParamsInit } from "react-router-dom";
import { ApprovedActivity } from "../Models/ApprovedActivity";

interface ISortOption {
  key: string;
  label: string;
  sortField: string;
}

interface IProps {
  activities: ApprovedActivity[];
  searchParams: URLSearchParams;
  setSearchParams: (nextInit: URLSearchParamsInit) => void;
}

const sortOptions: ISortOption[] = [
  { key: "organization", label: "Organization", sortField: "organization" },
  { key: "product", label: "Activity Name", sortField: "product" },
  { key: "certification", label: "Certification(s)", sortField: "certification" },
  { key: "eventStartDate", label: "Event Begin", sortField: "eventStartDate" },
  { key: "eventEndDate", label: "Event End", sortField: "eventEndDate" }
];

export default function ActivitiesTable(props: IProps) {
  const currentSortField = props.searchParams.get("SortField") ?? "organization";
  const currentSortDirection = props.searchParams.get("SortDirection") ?? "asc";

  const updateSort = (sortField: string) => {
    const next = new URLSearchParams(props.searchParams);
    const nextDirection = currentSortField === sortField && currentSortDirection !== "desc" ? "desc" : "asc";
    next.set("SortField", sortField);
    next.set("SortDirection", nextDirection);
    props.setSearchParams(next);
  };

  return (
    <div className="aa-results">
      <div className="aa-sort-toolbar" aria-label="Sort approved activities">
        <span className="aa-sort-toolbar__label">Sort by</span>
        <ButtonGroup className="aa-sort-toolbar__buttons">
          {sortOptions.map(option =>
            <Button
              key={option.key}
              variant={currentSortField === option.sortField ? "secondary" : "outline-secondary"}
              size="sm"
              onClick={() => updateSort(option.sortField)}
              aria-pressed={currentSortField === option.sortField}>
              {option.label}
              {currentSortField === option.sortField &&
                <span className="aa-sort-direction" aria-label={currentSortDirection === "desc" ? "descending" : "ascending"}>
                  {currentSortDirection === "desc" ? "v" : "^"}
                </span>
              }
            </Button>
          )}
        </ButtonGroup>
      </div>

      <div className="aa-card-grid">
        {props.activities.map(activity =>
          <article className="aa-activity-card" key={activity.approvedActivityId}>
            <div className="aa-activity-card__header">
              <div>
                <div className="aa-field-label">Activity Name</div>
                <h2 className="aa-activity-card__title">
                  {activity.productUrl ?
                    <a href={activity.productUrl} target="_blank" rel="noopener noreferrer">
                      {activity.productName}
                      <span className="aa-external-link-indicator" aria-hidden="true">
                        <svg viewBox="0 0 16 16" focusable="false">
                          <path className="aa-external-link-indicator__box" d="M2.5 5.5v8h8" />
                          <path d="M7 2.5h6.5V9" />
                          <path d="M6.5 9.5l7-7" />
                        </svg>
                      </span>
                      <span className="visually-hidden"> opens in a new tab</span>
                    </a> :
                    <span>{activity.productName}</span>
                  }
                </h2>
              </div>
            </div>

            <div className="aa-card-fields">
              <div className="aa-field aa-field--wide">
                <div className="aa-field-label">Organization</div>
                <div className="aa-field-value">{activity.organizationName}</div>
              </div>
              <div className="aa-field aa-field--wide">
                <div className="aa-field-label">Certification(s)</div>
                <div className="aa-field-value">{activity.appliesToAllCertifications ? "All" : activity.certificationNames}</div>
              </div>
              <div className="aa-field">
                <div className="aa-field-label">Event Begin</div>
                <div className="aa-field-value">{formatDate(activity.eventStartDate)}</div>
              </div>
              <div className="aa-field">
                <div className="aa-field-label">Event End</div>
                <div className="aa-field-value">{formatDate(activity.eventEndDate)}</div>
              </div>
            </div>

            <div className="aa-credit-panel" aria-label="Credit">
              <div className="aa-credit-panel__title">Credit</div>
              <div className="aa-credit-grid">
                <CreditValue label="CME" value={formatCredits(activity.cmeCredits)} />
                <CreditValue label="SA-CME" value={formatCredits(activity.saCredits)} />
                <CreditValue label="PIP Clinical" value={formatYesNo(activity.isPipClinical)} />
                <CreditValue label="PIP Feedback" value={formatYesNo(activity.isPipFeedback)} />
                <CreditValue label="Patient Safety" value={formatYesNo(activity.isPatientSafety)} />
              </div>
            </div>
          </article>
        )}
      </div>
    </div>
  );
}

function CreditValue(props: { label: string; value: string; }) {
  return (
    <div className="aa-credit-item">
      <span className="aa-credit-label">{props.label}</span>
      <span className="aa-credit-value">{props.value}</span>
    </div>
  );
}

function formatCredits(value?: number) {
  if (value === null || value === undefined) return "0";
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatDate(value: string) {
  if (!value) return "";
  const date = new Date(value);
  return date.toLocaleDateString();
}

function formatYesNo(value: boolean) {
  return value ? "Yes" : "No";
}
